import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Users,
  Mail,
  Shield,
  ShieldCheck,
  Pencil,
  Trash2,
  User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { sha256 } from "js-sha256";

const UsersPage = () => {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "vendedor",
    reference_code: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("No tienes permisos para ver los usuarios");
      } else {
        toast.error("Error al cargar usuarios");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingUser) {
        // Update user
        const updateData = {
          name: formData.name,
          email: formData.email,
          role: formData.role,
          reference_code: formData.reference_code,
        };
        if (formData.password) {
          let encryptedPassword = sha256(formData.password).toString();

          updateData.password = encryptedPassword;
        }
        console.log(updateData)
        await axios.put(`${API}/users/${editingUser.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Usuario actualizado exitosamente");
      } else {
        // Create user
        await axios.post(`${API}/users`, {
          ...formData,
          reference_code: formData.reference_code,
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Usuario creado exitosamente");
      }
      setShowModal(false);
      setEditingUser(null);
      setFormData({ name: "", email: "", password: "", role: "vendedor", reference_code: "" });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar usuario");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: "",
      role: user.role,
      reference_code: user.reference_code || "",
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    try {
      await axios.delete(`${API}/users/${deleteUser.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Usuario eliminado exitosamente");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar usuario");
    } finally {
      setDeleteUser(null);
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Gestión de Usuarios</h2>
          <p className="text-sm text-slate-500">{users.length} usuarios registrados</p>
        </div>
        <Button
          data-testid="add-user-btn"
          onClick={() => {
            setEditingUser(null);
            setFormData({ name: "", email: "", password: "", role: "vendedor", reference_code: "" });
            setShowModal(true);
          }}
          className="bg-[#0F172A] hover:bg-[#1E293B] text-white"
        >
          <Plus size={18} className="mr-2" />
          Nuevo Usuario
        </Button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <Input
          data-testid="user-search"
          placeholder="Buscar por nombre o email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 h-11"
        />
      </div>

      {/* Users Grid */}
      {filteredUsers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <Users size={32} className="text-slate-400" />
          </div>
          <h3 className="text-lg font-medium text-slate-800 mb-2">
            No hay usuarios
          </h3>
          <p className="text-slate-500 mb-4">
            {searchTerm
              ? "No se encontraron usuarios con ese criterio"
              : "Comienza agregando tu primer usuario"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
          {filteredUsers.map((user) => (
            <div
              key={user.id}
              data-testid={`user-card-${user.id}`}
              className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded flex items-center justify-center ${user.role === "admin" ? "bg-amber-100" : "bg-slate-100"
                    }`}>
                    {user.role === "admin" ? (
                      <ShieldCheck size={20} className="text-amber-600" />
                    ) : (
                      <User size={20} className="text-slate-600" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-800">{user.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${user.role === "admin"
                      ? "bg-amber-100 text-amber-700"
                      : "bg-blue-100 text-blue-700"
                      }`}>
                      {user.role === "admin" ? "Administrador" : "Vendedor"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-slate-600">
                  <Mail size={14} className="text-slate-400" />
                  <span className="truncate">{user.email}</span>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid={`edit-user-${user.id}`}
                  onClick={() => handleEdit(user)}
                  className="text-slate-600 hover:text-slate-800"
                >
                  <Pencil size={16} className="mr-1" />
                  Editar
                </Button>
                {user.id !== currentUser?.id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`delete-user-${user.id}`}
                    onClick={() => setDeleteUser(user)}
                    className="text-red-600 hover:text-red-800 hover:bg-red-50"
                  >
                    <Trash2 size={16} className="mr-1" />
                    Eliminar
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit User Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingUser ? "Editar Usuario" : "Nuevo Usuario"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="user-name">Nombre *</Label>
              <Input
                id="user-name"
                data-testid="user-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nombre completo"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-email">Email *</Label>
              <Input
                id="user-email"
                data-testid="user-email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="correo@ejemplo.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-password">
                {editingUser ? "Nueva Contraseña (dejar vacío para mantener)" : "Contraseña *"}
              </Label>
              <Input
                id="user-password"
                data-testid="user-password-input"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                required={!editingUser}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-role">Rol *</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue placeholder="Seleccionar rol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vendedor">
                    <div className="flex items-center gap-2">
                      <User size={16} />
                      Vendedor
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <ShieldCheck size={16} />
                      Administrador
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-reference-code">Código de Referencia</Label>
              <Input
                id="user-reference-code"
                data-testid="user-reference-code-input"
                value={formData.reference_code}
                onChange={(e) => setFormData({ ...formData, reference_code: e.target.value })}
                placeholder="Ej: REF-001"
              />
            </div>
            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                data-testid="save-user-btn"
                disabled={saving}
                className="bg-[#0F172A] hover:bg-[#1E293B]"
              >
                {saving ? "Guardando..." : editingUser ? "Actualizar" : "Crear Usuario"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que deseas eliminar a <strong>{deleteUser?.name}</strong>?
              Esta acción no se puede deshacer y los clientes asignados a este usuario
              quedarán sin asignar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsersPage;
